import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";

import { bridge } from "@multiweb/reactCore/bridge";
import { createSetFetchingAction } from "@multiweb/reactCore/fetching";
import { createLoadLocalizationKeysAction } from "@multiweb/reactCore/i18n";
import { IAggregationsResult } from "@multiweb/types/contracts";
import { IBigNumber } from "@multiweb/types/multiweb";
import { SagaWithReturnType, SagaYieldReturn, TypedObject } from "@multiweb/types/react";
import { createSetAggregationResultsAction } from "./actions";
import { AGGREGATION_FUNCTION_SELECTED, FETCHING_KEY_STATIC_ITEMS, FILTER_CHANGED, RELOAD_AGGREGATION_RESULTS } from "./constants";
import { SELECT_OPTION_LOC_KEY } from "./locKeys";
import { aggregationRowLogger } from "./logger";
import { OPTIONS_LOCALIZATION_KEYS } from "./options";
import { getClassName, getColumnAttributeName, getColumnFormat, getFilter, unsafeGetColumnsForReload } from "./selectors";
import {
    AggregationRowKey,
    ColumnKey,
    FormattedAggregationResults,
    IAggregationFunctionSelectedAction,
    IReloadAggregationResultsAction,
} from "./types";
import { createAggregationResultFetchingKey, createEmptyFormattedResults } from "./utils";

const { getDataReaderClient, getFormattedNumberString } = bridge;

export const saga: SagaWithReturnType = function* aggregationRowSaga() {
    yield fork(loadLocalizationsSaga);
    yield takeEvery(AGGREGATION_FUNCTION_SELECTED, aggregationFunctionSelectedSaga);
    yield takeEvery([RELOAD_AGGREGATION_RESULTS, FILTER_CHANGED], reloadAggregationResultsSaga);
};

/**
 * Načte lokalizace.
 */
export const loadLocalizationsSaga: SagaWithReturnType = function* loadLocalizationsSaga() {
    yield put(createLoadLocalizationKeysAction([...OPTIONS_LOCALIZATION_KEYS, SELECT_OPTION_LOC_KEY], FETCHING_KEY_STATIC_ITEMS));
};

/**
 * Obsluhuje výběr agregační funkce.
 * @param action
 */
export const aggregationFunctionSelectedSaga: SagaWithReturnType<[IAggregationFunctionSelectedAction]> = function* aggregationFunctionSelectedSaga(action) {
    const { columnKey, aggregationFunction } = action.payload;
    const { aggregationRowKey } = action.meta;
    if (!aggregationFunction) {
        return;
    }
    const className: ReturnType<typeof getClassName> = yield select(getClassName, { aggregationRowKey });
    if (!className) {
        yield call(aggregationRowLogger.error, "Cannot load aggregation result for aggregation row %s because it does not have defined className.", aggregationRowKey);
        return;
    }
    const attributeName: ReturnType<typeof getColumnAttributeName> = yield select(getColumnAttributeName, { aggregationRowKey, columnKey });
    if (!attributeName) {
        yield call(aggregationRowLogger.error, "Cannot load aggregation result for aggregation row %s because its column %s does not have defined attributeName.", aggregationRowKey, columnKey);
        return;
    }
    const { condition, conditionParams, readVersioned }: ReturnType<typeof getFilter> = yield select(getFilter, { aggregationRowKey });

    const fetchingKey: ReturnType<typeof createAggregationResultFetchingKey> = yield call(createAggregationResultFetchingKey, aggregationRowKey, columnKey);
    yield put(createSetFetchingAction(fetchingKey, true));

    try {
        const dataReaderClient: ReturnType<typeof getDataReaderClient> = yield call(getDataReaderClient);
        const result: SagaYieldReturn<typeof dataReaderClient.getAggregation> = yield call(dataReaderClient.getAggregation.bind(dataReaderClient), aggregationFunction, className, attributeName, condition, conditionParams, readVersioned);
        const formattedResult: SagaYieldReturn<typeof formatResultSaga> = yield call(formatResultSaga, aggregationRowKey, columnKey, result);
        yield put(createSetAggregationResultsAction(aggregationRowKey, { [columnKey]: formattedResult }));
    } catch (error) {
        yield call(
            aggregationRowLogger.error, "Loading of aggregation failed: %s. Input parameters: %O", error instanceof Error ? error?.message : "", {
                aggregationRowKey,
                className,
                columnKey,
                attributeName,
                aggregationFunction,
                condition,
                conditionParams,
                readVersioned,
            });
        // při chybě vymažeme předchozí výsledek, protože již není aktuální
        yield put(createSetAggregationResultsAction(aggregationRowKey, { [columnKey]: null }));
    } finally {
        yield put(createSetFetchingAction(fetchingKey, false));
    }
};

/**
 * Načte výsledky agregačních funkcí pro všechny numerické sloupce s vybranou agregační funkcí v jednom agregačním řádku.
 * @param action
 */
export const reloadAggregationResultsSaga: SagaWithReturnType<[IReloadAggregationResultsAction]> = function* reloadAggregationResultsSaga(action) {
    const { aggregationRowKey } = action.meta;
    const className: ReturnType<typeof getClassName> = yield select(getClassName, { aggregationRowKey });
    if (!className) {
        yield call(aggregationRowLogger.error, "Cannot load aggregation results for aggregation row %s because it does not have defined className.", aggregationRowKey);
        return;
    }
    const { condition, conditionParams, readVersioned }: ReturnType<typeof getFilter> = yield select(getFilter, { aggregationRowKey });

    const fetchingKey: ReturnType<typeof createAggregationResultFetchingKey> = yield call(createAggregationResultFetchingKey, aggregationRowKey);
    yield put(createSetFetchingAction(fetchingKey, true));
    const aggregationParams: ReturnType<typeof unsafeGetColumnsForReload> = yield select(unsafeGetColumnsForReload, { aggregationRowKey });
    try {
        const dataReaderClient: ReturnType<typeof getDataReaderClient> = yield call(getDataReaderClient);
        const results: SagaYieldReturn<typeof dataReaderClient.getAggregations> = yield call(dataReaderClient.getAggregations.bind(dataReaderClient), aggregationParams, className, condition, conditionParams, readVersioned);
        const formattedResults: SagaYieldReturn<typeof formatResultsSaga> = yield call(formatResultsSaga, aggregationRowKey, results);
        yield put(createSetAggregationResultsAction(aggregationRowKey, formattedResults));
    } catch (error) {
        yield call(aggregationRowLogger.error, "Loading of aggregations failed: %s. Input parameters: %O", error instanceof Error ? error?.message : "", {
            aggregationRowKey,
            className,
            condition,
            conditionParams,
            readVersioned,
        });
        // při chybě vymažeme předchozí výsledek, protože již není aktuální
        yield put(createSetAggregationResultsAction(aggregationRowKey, createEmptyFormattedResults(aggregationParams)));
    } finally {
        yield put(createSetFetchingAction(fetchingKey, false));
    }
};

/**
 * Naformátuje všechny výsledky.
 * @param aggregationRowKey Klíč agregačního řádku, do kterého patří sloupce, pro než se formátují výsledky.
 * @param results Výsledky k naformátování. Musí být klíčované přes klíč sloupce, aby bylo možné získat formát sloupce.
 * @return Naformátované výsledky.
 */
export const formatResultsSaga: SagaWithReturnType<[AggregationRowKey, IAggregationsResult], FormattedAggregationResults> = function* formatResultsSaga(aggregationRowKey, results) {
    const formattedAggregationResults: TypedObject<SagaYieldReturn<typeof formatResultSaga>> = yield all(
        Object.entries(results)
            .reduce((acc, [columnKey, result]) => ({
                ...acc,
                [columnKey]: call(formatResultSaga, aggregationRowKey, columnKey, result),
            }), {}),
    );
    return formattedAggregationResults;
};

/**
 * Naformátuje výsledek agregační funkce pro konkrétní sloupec.
 * @param aggregationRowKey Klíč agregačního řádku, do kterého sloupec patří.
 * @param columnKey Klíč sloupce, z něhož se načte formátovací řetězec.
 * @param result Hodnota k naformátování.
 * @return Naformátovaná hodnota v podobě textu.
 */
export const formatResultSaga: SagaWithReturnType<[AggregationRowKey, ColumnKey, IBigNumber], string> = function* formatResultSaga(aggregationRowKey, columnKey, result) {
    const displayFormat: SagaYieldReturn<typeof getColumnFormat> = yield select(getColumnFormat, { aggregationRowKey, columnKey });
    const numberString: SagaYieldReturn<typeof getFormattedNumberString> = yield call(getFormattedNumberString, result, { displayFormat });
    return numberString;
};
